import { Component, Input, OnInit } from '@angular/core';
import { Message } from 'src/app/interfaces/message';
import { StoreContextService } from 'src/app/services/store-context.service';

interface FlowEntry {
  label: string;
  value: string;
}

interface FlowProductView {
  name: string;
  image: string | null;
  detail: string | null;
}

const MONGO_ID_REGEX = /^[0-9a-f]{24}$/i;

// Claves tecnicas de los flows de Meta que no aportan nada al asesor
const SKIP_KEYS = new Set([
  'flow_token',
  'conversation_id',
  'product_conversation',
  'product_conversation_id',
  'conversation_product_id',
  'product',
  'error_message',
  'init_values',
  'next_step',
  'success',
  'superflow',
  'sign_up',
  'title_screen',
  'button_name',
  'button_form',
  'products_options',
  'products_text',
  'total_cart',
  'total_text',
  'total',
  'form_name',
  'checkbox_label',
  'similarity_stock',
  'schedule_visible',
  'footer_label',
  'inventory_status_medium',
  'inventory_status_large',
  'invetory_options',
  'screen',
  'version',
  'is_dollar',
  'payment_combined',
  'label_description',
  'cash_f',
  'flow_id',
]);

// Etiquetas en español para las claves conocidas de los formularios
const LABELS: Record<string, string> = {
  name: 'Nombre',
  last_name: 'Apellido',
  email: 'Correo',
  phone: 'Teléfono',
  document_type: 'Tipo de documento',
  document_id: 'Documento',
  doc_type: 'Tipo de documento',
  doc_number: 'Documento',
  gender: 'Género',
  birth_date: 'Fecha de nacimiento',
  quantity: 'Cantidad',
  notes: 'Notas',
  date: 'Fecha',
  time: 'Hora',
  coupon: 'Cupón',
  purchase_rating: 'Calificación',
  bank: 'Banco',
  cash: 'Paga con',
  payment_option_select: 'Método de pago',
  payment_type: 'Tipo de pago',
  payment_cashea_cedula: 'Cédula (Cashea)',
  comment_not_completed: 'Comentario',
  edit_cart: 'Editar carrito',
};

// Muestra las selecciones que el usuario envio en un flow de Meta
// (nfm_reply.response_json) mapeadas a etiquetas legibles; los ids de
// productos/extras se resuelven contra los eps de wink via StoreContextService
@Component({
  selector: 'app-flow-reply',
  templateUrl: './flow-reply.component.html',
})
export class FlowReplyComponent implements OnInit {
  @Input() message = {} as Message;

  title = 'Formulario';
  entries: FlowEntry[] = [];
  product: FlowProductView | null = null;
  products: FlowProductView[] = [];
  fallbackText: string | null = null;

  constructor(private storeContext: StoreContextService) {}

  async ngOnInit(): Promise<void> {
    const raw = this.message?.data?.nfm_reply?.response_json;
    let json: Record<string, unknown> | null = null;
    try {
      json = raw ? JSON.parse(raw) : null;
    } catch {
      json = null;
    }
    if (!json || typeof json !== 'object') {
      this.setFallback();
      return;
    }

    await this.resolveEntities(json);
    this.buildView(json);
    if (!this.entries.length && !this.product && !this.products.length && !this.fallbackText) {
      this.setFallback();
    }
  }

  private setFallback(): void {
    const body = this.message?.data?.nfm_reply?.body;
    this.fallbackText =
      body && body.toLowerCase() !== 'sent' ? body : 'Formulario enviado';
  }

  // Junta los ids de productos/extras presentes en la respuesta y los
  // resuelve en una sola llamada (el StoreContextService cachea resultados)
  private async resolveEntities(json: Record<string, unknown>): Promise<void> {
    const productIds: string[] = [];
    const productSelect = json['select_product_no_extra'] as
      | { id?: string }
      | undefined;
    if (productSelect?.id && MONGO_ID_REGEX.test(productSelect.id)) {
      productIds.push(productSelect.id);
    }

    const extraIds = this.collectOptionValues(json).filter(value =>
      MONGO_ID_REGEX.test(value)
    );

    if (productIds.length || extraIds.length) {
      await this.storeContext.resolveFlowEntities(productIds, extraIds);
    }
  }

  private collectOptionValues(json: Record<string, unknown>): string[] {
    const values: string[] = [];
    Object.keys(json).forEach(key => {
      if (!/^(radio_button|checkbox|checkbox_action)_\d+$/.test(key)) return;
      const value = json[key];
      if (typeof value === 'string' && value.length) {
        values.push(value);
      } else if (Array.isArray(value)) {
        value.forEach(item => {
          if (typeof item === 'string' && item.length) values.push(item);
        });
      }
    });
    return values;
  }

  private buildView(json: Record<string, unknown>): void {
    this.title = this.resolveTitle(json);
    if (this.buildSuperflowSelection(json)) return;

    // Flow de selección de método de pago (WINK-2093): se maneja aparte porque
    // el valor relevante es un id que solo se resuelve contra payment_options.
    if (this.buildPaymentSelection(json)) return;

    if (this.buildEditCartSelection(json)) return;

    // Flows de selección de producto (WINK-2093): similaridad y select-product
    // devuelven un "product" UUID de conversation_products que el front no resuelve;
    // el publisher inyecta el producto ya resuelto en message.data.selected_product.
    if (this.buildProductSelection(json)) return;

    const entries: FlowEntry[] = [];

    // Producto sin extras: la respuesta trae titulo/precio y el id permite
    // resolver la imagen desde el catalogo de la tienda
    const productSelect = json['select_product_no_extra'] as
      | { id?: string; title?: string; description?: string; price?: string }
      | undefined;
    if (productSelect) {
      const resolved = productSelect.id
        ? this.storeContext.getProduct(productSelect.id)
        : null;
      this.product = {
        name: resolved?.name || productSelect.title || 'Producto',
        image: resolved?.image ?? null,
        detail: productSelect.price || null,
      };
    }

    // Sede seleccionada: se mapea el id al nombre de la sede
    const inventoryValue = (json['inventory_select_large'] ||
      json['inventory_select']) as string | undefined;
    if (typeof inventoryValue === 'string' && inventoryValue.length) {
      entries.push({ label: 'Sede', value: this.mapInventory(inventoryValue) });
    }

    // Extras/opciones elegidas en formularios de producto configurable
    const optionValues = this.collectOptionValues(json);
    if (optionValues.length) {
      const names = optionValues.map(
        value => this.storeContext.getExtra(value)?.name || value
      );
      entries.push({ label: 'Extras', value: names.join(', ') });
    }

    // Resto de claves conocidas o genericas
    Object.keys(json).forEach(key => {
      if (
        SKIP_KEYS.has(key) ||
        key === 'select_product_no_extra' ||
        key === 'inventory_select' ||
        key === 'inventory_select_large' ||
        /^(radio_button|checkbox|checkbox_action)_\d+$/.test(key)
      ) {
        return;
      }
      const value = this.formatValue(key, json[key]);
      if (value === null) return;
      entries.push({ label: LABELS[key] ?? this.humanize(key), value });
    });

    this.entries = entries;
  }

  // Superflow devuelve UUIDs product_conversation_1..8 resueltos por el
  // publisher en message.data.selected_products (lista), renderizada como lista
  // de productos.
  private buildSuperflowSelection(json: Record<string, unknown>): boolean {
    if (json['superflow'] !== true) return false;

    const selectedProducts = this.message?.data?.selected_products;
    this.title = 'Productos seleccionados';
    if (selectedProducts?.length) {
      this.products = selectedProducts.map(item => ({
        name: item.name,
        image: item.image ?? null,
        detail: item.price ?? null,
      }));
    } else {
      this.fallbackText = 'Selección de productos';
    }
    return true;
  }

  // Selección de método de pago: el id elegido llega en payment_option_select y
  // su nombre legible puede venir enriquecido o en payment_options embebida.
  // Devuelve true si el mensaje corresponde a este flow y ya construyó la vista.
  private buildPaymentSelection(json: Record<string, unknown>): boolean {
    const selectedId = json['payment_option_select'];
    const screenData = (json['screen_data'] ?? null) as Record<
      string,
      unknown
    > | null;
    const rawOptions =
      screenData?.['payment_options'] ?? json['payment_options'];
    if (typeof selectedId !== 'string' || !selectedId) {
      return false;
    }

    const options = Array.isArray(rawOptions)
      ? (rawOptions as Array<{ id?: string; title?: string }>)
      : [];
    const selectedPayment = this.message?.data?.selected_payment;
    const method =
      (selectedPayment?.name ||
        options.find(option => option?.id === selectedId)?.title) ??
      'Método seleccionado';

    const entries: FlowEntry[] = [{ label: 'Método', value: method }];

    const amount = this.buildPaymentAmount(screenData);
    if (amount) entries.push({ label: 'Monto', value: amount });

    const taxDocument = this.formatValue(
      'tax_document',
      json['tax_document'] || json['tax_document_input']
    );
    if (taxDocument) {
      entries.push({ label: 'Documento fiscal', value: taxDocument });
    }

    const taxName = this.formatValue(
      'tax_name',
      json['tax_name'] || json['tax_name_input']
    )?.trim();
    if (taxName) entries.push({ label: 'Nombre fiscal', value: taxName });

    if (typeof json['receipt_checkbox'] === 'boolean') {
      entries.push({
        label: 'Factura personalizada',
        value: json['receipt_checkbox'] ? 'Sí' : 'No',
      });
    }

    this.entries = entries;
    return true;
  }

  // Los montos vienen preformateados en screen_data como "Monto ($): $10.30" /
  // "Monto (Bs.): Bs.7.837,79"; se quita la etiqueta previa a los dos puntos y
  // se combinan los dos montos disponibles.
  private buildPaymentAmount(
    screenData: Record<string, unknown> | null
  ): string | null {
    if (!screenData) return null;
    const parts: string[] = [];
    ['dolar_payment_text', 'local_payment_text'].forEach(key => {
      const text = screenData[key];
      if (typeof text !== 'string' || !text.length) return;
      const value = text.includes(':')
        ? text.slice(text.indexOf(':') + 1).trim()
        : text.trim();
      if (value) parts.push(value);
    });
    return parts.length ? parts.join(' · ') : null;
  }

  // Edición de carrito: el publisher resuelve product_conversation e inyecta
  // message.data.selected_product para mostrar el producto editado.
  private buildEditCartSelection(json: Record<string, unknown>): boolean {
    if (json['edit_product'] === undefined) return false;

    this.title = 'Edición del carrito';
    const selected = this.message?.data?.selected_product;
    if (selected?.name) {
      this.product = {
        name: selected.name,
        image: selected.image ?? null,
        detail: selected.price ?? null,
      };
    }

    const entries: FlowEntry[] = [];
    const quantity = this.formatValue('quantity', json['quantity']);
    if (quantity !== null) entries.push({ label: 'Cantidad', value: quantity });

    const notes = this.formatValue('notes', json['notes']);
    if (notes !== null) entries.push({ label: 'Notas', value: notes });

    this.entries = entries;
    return true;
  }

  // Selección de producto (similaridad y select-product): el producto elegido lo
  // resuelve el publisher y llega en message.data.selected_product (nombre + precio
  // ya formateado). Si no vino (mensaje viejo o no resoluble) se muestra un texto
  // claro. No maneja select_product_no_extra, que tiene su propio flujo abajo.
  private buildProductSelection(json: Record<string, unknown>): boolean {
    if (json['select_product_no_extra']) return false;
    const selected = this.message?.data?.selected_product;
    const isSimilarity = json['similarity_stock'] === true;
    const isProductFlow = isSimilarity || typeof json['product'] === 'string';
    if (!selected && !isProductFlow) return false;

    this.title = isSimilarity ? 'Producto seleccionado' : 'Producto agregado';
    if (selected?.name) {
      this.product = {
        name: selected.name,
        image: selected.image ?? null,
        detail: selected.price ?? null,
      };
    } else {
      this.fallbackText = 'Selección de producto';
    }
    return true;
  }

  private resolveTitle(json: Record<string, unknown>): string {
    if (json['sign_up'] != undefined) return 'Registro de usuario';
    if (json['select_product_no_extra'] || json['product'] != undefined)
      return 'Selección de producto';
    if (json['edit_cart'] != undefined || json['edit_product'] != undefined)
      return 'Edición del carrito';
    if (json['inventory_select'] || json['inventory_select_large'])
      return 'Selección de sede';
    if (json['coupon'] != undefined) return 'Cupón de descuento';
    if (json['bank'] != undefined) return 'Reporte de pago móvil';
    if (
      json['payment_option_select'] != undefined ||
      json['cash'] != undefined ||
      json['cash_f'] != undefined ||
      json['payment_cashea_cedula'] != undefined
    )
      return 'Método de pago';
    if (json['purchase_rating'] != undefined)
      return 'Calificación de la compra';
    if (json['date'] != undefined && json['time'] != undefined)
      return 'Programación del pedido';
    return 'Formulario';
  }

  private mapInventory(value: string): string {
    if (value === 'nearest-location-inventory') return 'Sede más cercana';
    const id = value.startsWith('inventory-change-')
      ? Number(value.replace('inventory-change-', ''))
      : Number(value.split('-')[1]);
    if (Number.isFinite(id)) {
      return this.storeContext.getInventoryName(id) ?? `Sede #${id}`;
    }
    return value;
  }

  private formatValue(key: string, value: unknown): string | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'boolean') return value ? 'Sí' : 'No';
    if (typeof value === 'number') return String(value);
    if (typeof value === 'string') {
      if (key === 'edit_cart') return value === '1' ? 'Sí' : 'No';
      if (key === 'date') return this.formatFlowDate(value);
      if (key === 'time') return this.formatFlowTime(value);
      // Ids crudos (uuid/mongo) no le dicen nada al asesor
      if (MONGO_ID_REGEX.test(value) || /^[0-9a-f-]{36}$/i.test(value)) {
        return null;
      }
      return value;
    }
    if (Array.isArray(value)) {
      const items = value.filter(item => typeof item === 'string');
      return items.length ? items.join(', ') : null;
    }
    return null;
  }

  private formatFlowDate(value: string): string {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return value;
    const [, year, month, day] = match;
    return `${day}/${month}/${year}`;
  }

  private formatFlowTime(value: string): string {
    const match = value.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return value;
    const hours = Number(match[1]);
    const minutes = match[2];
    const suffix = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 === 0 ? 12 : hours % 12;
    return `${String(hours12).padStart(2, '0')}:${minutes} ${suffix}`;
  }

  private humanize(key: string): string {
    const text = key.replace(/_/g, ' ').trim();
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
}
